import { useEffect, useRef } from 'react';
import { API_BASE_URL } from '../services/apiClient';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  enqueueNotification,
  setStreamStatus,
  updateHeartbeat,
} from '../redux/notificationsSlice';
import { NotificationEvent } from '../types';

const NOTIFICATION_ENDPOINT = `${API_BASE_URL.replace(/\/$/, '')}/notifications/stream`;
const HANDLED_EVENT_TYPES = [
  'message',
  'calendar-reminder',
  'calendar-generated',
  'acupoint-plan',
  'tizhi-update',
  'tizhi-alert',
];

export function useNotificationStream() {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const sourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!accessToken) {
      sourceRef.current?.close();
      sourceRef.current = null;
      dispatch(setStreamStatus('idle'));
      return;
    }

    const streamUrl = `${NOTIFICATION_ENDPOINT}?token=${encodeURIComponent(accessToken)}`;
    const source = new EventSource(streamUrl);
    sourceRef.current = source;
    let closed = false;

    dispatch(setStreamStatus('connecting'));

    source.onopen = () => {
      if (closed) {
        return;
      }
      dispatch(setStreamStatus('connected'));
    };

    const handleHeartbeat = (event: MessageEvent) => {
      if (closed) {
        return;
      }
      let timestamp = new Date().toISOString();
      if (event.data) {
        try {
          const parsed = JSON.parse(event.data) as { timestamp?: string };
          if (parsed?.timestamp) {
            timestamp = parsed.timestamp;
          }
        } catch (error) {
          console.warn('Failed to parse heartbeat payload', error, event.data);
        }
      }
      dispatch(updateHeartbeat(timestamp));
    };

    const handleEvent = (event: MessageEvent) => {
      if (closed) {
        return;
      }
      if (event.type === 'connected') {
        return;
      }
      if (!event.data) {
        return;
      }

      try {
        const parsed = JSON.parse(event.data) as Partial<NotificationEvent> | { payload?: unknown };
        const payload = parsed as Partial<NotificationEvent> & { payload?: unknown };
        const notification: NotificationEvent = {
          id: payload.id ?? crypto.randomUUID(),
          type: payload.type ?? event.type ?? 'message',
          payload: payload.payload ?? parsed,
          timestamp: payload.timestamp ?? new Date().toISOString(),
        };
        dispatch(enqueueNotification({ event: notification }));
      } catch (error) {
        console.warn('Failed to parse notification event', error, event.data);
      }
    };

    source.addEventListener('heartbeat', handleHeartbeat as EventListener);
    HANDLED_EVENT_TYPES.forEach((eventType) => {
      source.addEventListener(eventType, handleEvent as EventListener);
    });
    source.addEventListener('connected', handleEvent as EventListener);

    source.onerror = (event) => {
      if (closed) {
        return;
      }
      console.warn('Notification stream error', event);
      dispatch(setStreamStatus('connecting'));
    };

    return () => {
      closed = true;
      source.removeEventListener('heartbeat', handleHeartbeat as EventListener);
      HANDLED_EVENT_TYPES.forEach((eventType) => {
        source.removeEventListener(eventType, handleEvent as EventListener);
      });
      source.removeEventListener('connected', handleEvent as EventListener);
      source.close();
      sourceRef.current = null;
      dispatch(setStreamStatus('idle'));
    };
  }, [accessToken, dispatch]);
}
